/**
 * Daily submission wiring (TRD-10 §5): the glue between a finished run and
 * `submitRun`, plus the rule about which attempts are worth sending.
 *
 * R-10-15 originally read: one SUBMITTED attempt per player per day, later attempts
 * practice only. That rule WAS the "my best run never showed up on the leaderboard" bug.
 * The server has always upserted keeping the better score, so the day's first attempt was
 * the only one that could ever set the row — play a mediocre run, and every better run
 * after it was thrown away on the client without ever reaching the service.
 *
 * The rule now is: send an attempt when it would actually change the board. One row per
 * player per day still holds (the server's upsert guarantees it, not the client), and the
 * replay pool still cannot be spammed with runs that could not improve anything.
 */
import { SIM_VERSION, type SubmissionPayload, type TimedCommand, type World } from '@prism/sim';
import type { DailyHistoryEntry, SaveData } from './save.js';

/** Sent with every submission; bump alongside releases worth distinguishing in the DB. */
export const CLIENT_VERSION = '0.1.0';

/** The best score already SENT for this date, or null when nothing has been. */
export function bestSubmittedScore(save: SaveData, date: string): number | null {
  let best: number | null = null;
  for (const entry of save.dailyHistory) {
    if (entry.date !== date || !entry.submitted) continue;
    if (best === null || entry.score > best) best = entry.score;
  }
  return best;
}

/**
 * True when this finished run is worth sending: nothing submitted today, or it beat what
 * was. A run that ties the best is not sent — it cannot move the row, and the tie-break
 * is earliest-submission, so re-sending it could only ever cost the player places.
 */
export function shouldSubmit(save: SaveData, date: string, score: number): boolean {
  const best = bestSubmittedScore(save, date);
  return best === null || score > best;
}

/** The payload the server re-simulates, assembled from the ended world and its log. */
export function buildDailyPayload(
  world: World,
  commands: readonly TimedCommand[],
  save: SaveData,
  date: string,
): SubmissionPayload {
  return {
    date,
    playerId: save.playerId,
    displayName: save.displayName || 'anonymous',
    claimedScore: world.score,
    // The ROLLING digest, never hash() (TRD-00 R-00-08) — hash() is a point-in-time
    // probe; runHash is the digest the server's replay reproduces tick by tick.
    runHash: world.runHash,
    simVersion: SIM_VERSION,
    clientVersion: CLIENT_VERSION,
    finalTick: world.tick,
    // The shop is a per-RUN draw now, not a property of the day, so the seed it rolled
    // from has to travel or the server's replay would offer different towers.
    shopSeed: world.shopSeed.toString(),
    commands: [...commands],
  };
}

/** Fold the attempt into the save's daily history, marking whether it was sent. */
export function recordDailyAttempt(
  save: SaveData,
  entry: Omit<DailyHistoryEntry, 'submitted'>,
  submitted: boolean,
): SaveData {
  return {
    ...save,
    dailyHistory: [...save.dailyHistory, { ...entry, submitted }],
  };
}
